var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-407 — the help wording NK changed in Settings, read from
 * `GET /czna-authorities/help-texts` (every role holds that permission).
 *
 * CZNA-1340 — the same response carries `subfields`: what each subfield means in its field
 * (`100 $c`), shown next to that subfield in the record form. One request and one cache entry
 * for both, so saving either invalidates one key (`helpTextsQueryKey`).
 *
 * Help must never depend on this request: any failure — 403 for a user whose capability
 * sync has not run yet, a network error, an older backend without the route — yields no
 * overrides and no subfield descriptions, and the packaged texts render as they did before
 * CZNA-407.
 *
 * MEMORY rule: `useOkapiKy()` returns an unstable reference; it is captured in the query
 * function closure and never listed in a dependency array.
 */
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { getHeaders } from '../api/apiUtils';
/** Saving `help_text.overrides` invalidates this key (see `moduleSettingInvalidationKeys`). */
export var helpTextsQueryKey = ['help-texts'];
export var HELP_TEXTS_PATH = 'czna-authorities/help-texts';
/** Key of one subfield in the description map — the field matters, `100 $c` ≠ `400 $c`. */
export var subfieldHelpKey = function (tag, code) { return "".concat(tag, "$").concat(code); };
var NO_OVERRIDES = {};
var NO_SUBFIELDS = {};
var NOTHING = { texts: NO_OVERRIDES, subfields: NO_SUBFIELDS };
var isSubfieldHelp = function (value) {
    var entry = value;
    return !!entry && typeof entry.tag === 'string' && typeof entry.code === 'string'
        && typeof entry.description === 'string';
};
var normalise = function (response) {
    var texts = (response === null || response === void 0 ? void 0 : response.texts) && typeof response.texts === 'object' ? response.texts : NO_OVERRIDES;
    var list = Array.isArray(response === null || response === void 0 ? void 0 : response.subfields) ? response.subfields : [];
    var subfields = {};
    list.filter(isSubfieldHelp).forEach(function (entry) {
        subfields[subfieldHelpKey(entry.tag, entry.code)] = entry;
    });
    return { texts: texts, subfields: list.length ? subfields : NO_SUBFIELDS };
};
var useHelpTextsData = function () {
    var ky = useOkapiKy();
    var data = useQuery(helpTextsQueryKey, function () { return __awaiter(void 0, void 0, void 0, function () {
        var response, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, ky.get(HELP_TEXTS_PATH, { headers: getHeaders() })
                            .json()];
                case 1:
                    response = _b.sent();
                    return [2 /*return*/, normalise(response)];
                case 2:
                    _a = _b.sent();
                    return [2 /*return*/, NOTHING];
                case 3: return [2 /*return*/];
            }
        });
    }); }, { retry: false }).data;
    return data !== null && data !== void 0 ? data : NOTHING;
};
var useHelpTexts = function () { return useHelpTextsData().texts; };
/** CZNA-1340 — every stored subfield description, keyed by `subfieldHelpKey`. */
export var useSubfieldDescriptions = function () { return useHelpTextsData().subfields; };
export default useHelpTexts;
